import { createClient } from 'contentful'

const DOCUMENT_CONTENT_TYPE = 'document'
const TOOLBOX_DOCUMENT_TYPE_FILTER = 'Toolbox'
const TRAINING_DOCUMENT_TYPE_FILTER = 'Training'
const INSIGHT_UNDERSTANDING_DOCUMENT_TYPE_FILTER = 'Insight'
const INTRO_UNDERSTANDING_DOCUMENT_TYPE_FILTER = 'Intro'
const TOOLBOX_DOCUMENT_CATEGORY_CONTENT_TYPE = 'documentToolboxCategory'
const TRAINING_DOCUMENT_CATEGORY_CONTENT_TYPE = 'documentTrainingCategory'
const INSIGHT_DOCUMENT_CATEGORY_CONTENT_TYPE = 'documentInsightsCategory'
const INTRO_DOCUMENT_CATEGORY_CONTENT_TYPE = 'documentIntroCategory'
const DOCUMENT_SUB_CATEGORY_CONTENT_TYPE = 'documentSubCategory'
const WHATS_NEW_CONTENT_TYPE = 'newsItem'
const WHATS_NEW_CATEGORY_CONTENT_TYPE = 'newsCategory'
const SUBSCRIPTION_PLAN_CONTENT_TYPE = 'subscriptionPlan'
const FAQ_CONTENT_TYPE = 'faq'

const getClient = () => {
    return createClient({
        space: process.env.REACT_APP_CONTENTFUL_SPACE_ID,
        accessToken: process.env.REACT_APP_CONTENTFUL_DELIVERY_API_KEY,
        environment: process.env.REACT_APP_CONTENTFUL_ENVIRONMENT || 'master',
    })
}

const GetDateFormattedForContentful = () => {
    var todaysDate = new Date(),
        month = '' + (todaysDate.getMonth() + 1),
        day = '' + todaysDate.getDate(),
        year = todaysDate.getFullYear()

    if (month.length < 2) month = '0' + month
    if (day.length < 2) day = '0' + day

    return [year, month, day].join('-')
}

const GetAllFaq = async () => {
    const client = getClient()
    const documents = await client.getEntries({
        content_type: FAQ_CONTENT_TYPE,
    })
    return documents.items
}

const GetDocuments = async (searchString, selectedCategory, documentType, categoryType) => {
    const client = getClient()

    let query = {
        content_type: DOCUMENT_CONTENT_TYPE,
        order: 'fields.order',
    }

    query['fields.documentType'] = documentType

    if (searchString) {
        query['query'] = searchString
    }

    if (selectedCategory) {
        query['fields.category.sys.contentType.sys.id'] = categoryType
        query['fields.category.fields.name'] = selectedCategory
    }
    const documents = await client.getEntries(query)
    return documents.items
}

const GetToolboxDocuments = async (searchString, selectedCategory) => {
    return await GetDocuments(
        searchString,
        selectedCategory,
        TOOLBOX_DOCUMENT_TYPE_FILTER,
        TOOLBOX_DOCUMENT_CATEGORY_CONTENT_TYPE
    )
}

const GetTrainingDocuments = async (searchString, selectedCategory) => {
    return await GetDocuments(
        searchString,
        selectedCategory,
        TRAINING_DOCUMENT_TYPE_FILTER,
        TRAINING_DOCUMENT_CATEGORY_CONTENT_TYPE
    )
}

const GetInsightUnderstandingDocuments = async () => {
    return await GetDocuments(null, null, INSIGHT_UNDERSTANDING_DOCUMENT_TYPE_FILTER)
}

const GetIntroDocument = async (type) => {
    let introDocs = []

    if (!type) {
        return GetDefaultIntroDocument()
    }

    introDocs = await GetDocuments(
        null,
        type,
        INTRO_UNDERSTANDING_DOCUMENT_TYPE_FILTER,
        INTRO_DOCUMENT_CATEGORY_CONTENT_TYPE
    )

    if (introDocs.length === 0) {
        return GetDefaultIntroDocument()
    }

    return introDocs[0]
}

const GetDefaultIntroDocument = async () => {
    const introDocs = await GetDocuments(
        null,
        'Intro',
        INTRO_UNDERSTANDING_DOCUMENT_TYPE_FILTER,
        INTRO_DOCUMENT_CATEGORY_CONTENT_TYPE
    )

    if (introDocs.length === 0) return null
    return introDocs[0]
}

const GetDocumentCategories = async (contentType) => {
    const client = getClient()
    const categories = await client.getEntries({
        content_type: contentType,
        order: 'fields.order',
    })
    return categories.items
}

const GetToolboxDocumentCategories = async () => {
    return await GetDocumentCategories(TOOLBOX_DOCUMENT_CATEGORY_CONTENT_TYPE)
}

const GetTrainingDocumentCategories = async () => {
    return await GetDocumentCategories(TRAINING_DOCUMENT_CATEGORY_CONTENT_TYPE)
}

const GetInsightsDocumentCategories = async () => {
    return await GetDocumentCategories(INSIGHT_DOCUMENT_CATEGORY_CONTENT_TYPE)
}

const GetTrainingDocumentSubCategories = async () => {
    return await GetDocumentCategories(DOCUMENT_SUB_CATEGORY_CONTENT_TYPE)
}

const GetWhatsNewCategories = async () => {
    const client = getClient()
    const categories = await client.getEntries({
        content_type: WHATS_NEW_CATEGORY_CONTENT_TYPE,
    })
    return categories.items
}

const GetWhatsNew = async (searchString, selectedCategory) => {
    const client = getClient()
    let query = {
        content_type: WHATS_NEW_CONTENT_TYPE,
        'fields.publishedDate[lte]': GetDateFormattedForContentful(),
        order: '-fields.publishedDate',
    }

    if (searchString) {
        query['query'] = searchString
    }

    if (selectedCategory) {
        query['fields.category.sys.contentType.sys.id'] = WHATS_NEW_CATEGORY_CONTENT_TYPE
        query['fields.category.fields.name'] = selectedCategory
    }

    const news = await client.getEntries(query)
    return news.items
}

const GetWhatsNewItem = async (slug) => {
    const client = getClient()
    const news = await client.getEntries({
        content_type: WHATS_NEW_CONTENT_TYPE,
        'fields.publishedDate[lte]': GetDateFormattedForContentful(),
        'fields.slug': slug,
    })

    if (news.items.length === 0) return null
    return news.items[0]
}

const GetLatestWhatsNewItem = async () => {
    const client = getClient()
    const news = await client.getEntries({
        content_type: WHATS_NEW_CONTENT_TYPE,
        'fields.publishedDate[lte]': GetDateFormattedForContentful(),
        order: '-fields.publishedDate',
        limit: 1,
    })

    if (news.items.length === 0) return null
    return news.items[0]
}

const GetSubscriptionPlans = async (selected, freePlanOverrideId) => {
    const client = getClient()
    let plans = {}

    let query = {
        content_type: SUBSCRIPTION_PLAN_CONTENT_TYPE,
        order: 'fields.rank',
    }

    if (freePlanOverrideId) {
        query = {
            ...query,
            'fields.stripePlanId': freePlanOverrideId,
        }
    } else {
        // only display ones
        query = {
            ...query,
            'fields.display': 1,
        }
    }

    plans = await client.getEntries(query)

    return plans.items
}

export {
    GetToolboxDocuments,
    GetTrainingDocuments,
    GetInsightUnderstandingDocuments,
    GetToolboxDocumentCategories,
    GetTrainingDocumentCategories,
    GetInsightsDocumentCategories,
    GetWhatsNew,
    GetWhatsNewCategories,
    GetWhatsNewItem,
    GetLatestWhatsNewItem,
    GetSubscriptionPlans,
    GetAllFaq,
    GetIntroDocument,
    GetTrainingDocumentSubCategories,
}
